import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { NeptuneApi } from '../genericApi/neptune.api';
import { DataConnectorApiModel } from './models/data-connector-api.model';
import { GoogleAnalyticsEntityApiModel } from './models/google-analytics-entity-api.model';
import { ConfigService } from '../../Core/Config/services/config.service';

@Injectable()
export class DataConnectorsApi {
    private apiUrl: string;

    constructor(
        private neptuneApi: NeptuneApi,
        private configService: ConfigService
    ) {
        this.apiUrl = this.configService.getConfig().apiUrl;
    }

    /**
     * Gets a list of limited report information
     */
    public getAll(): Observable<DataConnectorApiModel[]> {
        return this.neptuneApi.api<DataConnectorApiModel[]>(
            '/dataconnectors',
            null,
            HttpVerbsEnum.GET
        );
    }

    public getDataConnector(accountId: number, dataConnectorId: number) {
        return this.neptuneApi.api<DataConnectorApiModel>(
            `/accounts/${accountId}/dataconnectors/${dataConnectorId}`,
            null,
            HttpVerbsEnum.GET
        );
    }

    public createDataConnector(dataConnectorModel: DataConnectorApiModel, apiKeyFile: File): Observable<number> {
        const formData: FormData = new FormData();
        formData.append('modelJson', JSON.stringify(dataConnectorModel));
        formData.append('apiKeyFile', apiKeyFile);

        // neptuneApi.api( ... ) assumes you are sending content-type: json, we are using multipart form data
        return this.neptuneApi.httpClient.post<number>(
            this.apiUrl + '/dataconnectors',
            formData,
            {
                withCredentials: true
            }
        );
    }

    public updateDataConnector(dataConnectorId: number, dataConnectorModel: DataConnectorApiModel, apiKeyFile: File): Observable<void> {
        const formData: FormData = new FormData();
        formData.append('modelJson', JSON.stringify(dataConnectorModel));
        formData.append('apiKeyFile', apiKeyFile);

        // neptuneApi.api( ... ) assumes you are sending content-type: json, we are using multipart form data
        return this.neptuneApi.httpClient.put<void>(
            this.apiUrl + `/dataconnectors/${dataConnectorId}`,
            formData,
            {
                withCredentials: true
            }
        );
    }

    public patchDataConnector(dataConnectorId: number, dataConnectorModel: DataConnectorApiModel): Observable<void> {
        return this.neptuneApi.api(
            `/dataconnectors/${dataConnectorId}`,
            dataConnectorModel,
            HttpVerbsEnum.PATCH
        );
    }

    public deleteDataConnector(dataConnectorId: number) {
        return this.neptuneApi.api<void>(
            `/dataconnectors/${dataConnectorId}`,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    /**
     * Gets a list of limited report information
     */
    public getGoogleAnalyticsAccounts(dataConnectorId: number): Observable<GoogleAnalyticsEntityApiModel[]> {
        return this.neptuneApi.api<GoogleAnalyticsEntityApiModel[]>(
            `/dataconnectors/${dataConnectorId}/googleanalytics`,
            {
                type: 'ACCOUNT'
            },
            HttpVerbsEnum.POST
        );
    }

    /**
     * Gets a list of limited report information
     */
    public getGoogleAnalyticsProfiles(dataConnectorId: number, accountId: string, webPropertyId?: string): Observable<GoogleAnalyticsEntityApiModel[]> {
        const body: any = {
            type: 'PROFILE'
        };
        if (accountId) {
            body.accountId = accountId;
        }

        if (webPropertyId) {
            body.webPropertyId = webPropertyId;
        }

        return this.neptuneApi.api<GoogleAnalyticsEntityApiModel[]>(
            `/dataconnectors/${dataConnectorId}/googleanalytics`,
            body,
            HttpVerbsEnum.POST
        );
    }

    public getGoogleAnalyticsProperties(dataConnectorId: number, accountId: string): Observable<GoogleAnalyticsEntityApiModel[]> {
        const body: any = {
            type: 'WEB_PROPERTY'
        };
        if (accountId) {
            body.accountId = accountId;
        }
        return this.neptuneApi.api<GoogleAnalyticsEntityApiModel[]>(
            `/dataconnectors/${dataConnectorId}/googleanalytics`,
            body,
            HttpVerbsEnum.POST
        );
    }
}
